import {Paper, Box, Typography, Button, Alert, TextField} from '@mui/material'
import {useNavigate} from 'react-router-dom'
import {useFormik} from 'formik'
import * as yup from 'yup'
import CircularProgress from '@mui/material/CircularProgress'
import * as authAPI from '../AuthApi'
import {useFormikTextFieldHelpers} from '../../../components/hooks/useFormikTextFieldHelpers'
import {useAlerts} from '../../common/components/alerts/useAlerts'

export const ResetPasswordPage = () => {
  const navigate = useNavigate()
  const {pushError} = useAlerts()

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      try {
        await authAPI.resetPassword(values.password)
        navigate('/')
      } catch (e) {
        pushError(e)
      }
    },
  })
  const formikTextInputHelper = useFormikTextFieldHelpers(formik)

  return (
    <Paper
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        overflow: 'hidden',
        width: '80vw',
        maxWidth: '30rem',
      }}
    >
      <Box
        sx={(theme) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          textAlign: 'center',
          textTransform: 'uppercase',
          p: theme.spacing(2),
        })}
      >
        <Typography
          component='h1'
          variant='h6'
          sx={{
            letterSpacing: '0.1em',
            fontWeight: '700',
          }}
        >
          Reset your password
        </Typography>
      </Box>
      <Box
        component='form'
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          padding: theme.spacing(5),
          [theme.breakpoints.down('md')]: {
            padding: theme.spacing(3),
          },
          [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2),
          },
        })}
        autoComplete='off'
        onSubmit={formik.handleSubmit}
      >
        {formik.status && (
          <Alert severity='success' sx={(theme) => ({mb: theme.spacing(3)})}>
            {formik.status}
          </Alert>
        )}
        <TextField
          InputLabelProps={{
            shrink: true,
          }}
          autoFocus
          label='Password'
          placeholder='Enter Password'
          fullWidth
          id='field-password'
          type='password'
          {...formikTextInputHelper.getFieldProps('password')}
        />
        <TextField
          id='field-password-repeat'
          InputLabelProps={{
            shrink: true,
          }}
          autoFocus
          label='Repeat Password'
          placeholder='Enter Password'
          fullWidth
          type='password'
          {...formikTextInputHelper.getFieldProps('repeatPassword')}
        />
        <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
          <Button
            type='button'
            variant='contained'
            color='primary'
            onClick={() => navigate('/auth/login')}
            sx={(theme) => ({mr: theme.spacing(1)})}
            id='button-back'
          >
            Back
          </Button>
          <Button
            disabled={formik.isSubmitting || !formik.isValid}
            type='submit'
            variant='contained'
            color='primary'
            sx={{
              position: 'relative',
            }}
            id='button-back'
          >
            Submit
            {formik.isSubmitting && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Button>
        </Box>
      </Box>
    </Paper>
  )
}

const initialValues = {
  password: '',
  repeatPassword: '',
}

const validationSchema = yup.object().shape({
  password: yup
    .string()
    .required('Provide a valid password.')
    .min(8, 'Minimum of 8 characters required.'),
  repeatPassword: yup
    .string()
    .test('password-match', 'Password does not match.', (value, context) => {
      const form = context.parent as typeof initialValues
      return value === form.password
    }),
})

export default ResetPasswordPage
